// Apolo IDE — useCostMeter hook (Sprint v4 Epic S5 WS S5.2).
//
// Polls GET /api/agents/cost/current at a fixed cadence and exposes a
// shape ready for the CostMeter component. Live update real-time +
// alert when 80% is reached are the PRE-RESOLVED defaults of S5.
//
// API:
//   useCostMeter(options?)
//   options.pollIntervalMs  — default 2000 (the panel needs live feel,
//                              but we don't need millisecond accuracy).
//   options.fetcher         — apiFetch-shaped function (for tests).
//   options.enabled         — default true; when false the hook does not
//                              start polling (useful when panel is closed
//                              so we don't waste fetches).
//
// Returns:
//   {
//     snapshot,    // raw response payload or null
//     loading,
//     error,
//     refresh,     // manual refetch (returns the snapshot)
//     start,       // restart polling explicitly
//     stop,        // pause polling
//     alert,       // convenience: snapshot?.alert === true
//     warning,     // convenience: snapshot?.warning === true
//     pct,         // convenience: snapshot?.pct ?? 0
//   }
//
// The hook does NOT throw on transient network errors — it surfaces
// them via `error` and keeps polling. The CostMeter component should
// degrade gracefully (show "—" while waiting).

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

const DEFAULT_POLL_MS = 2000;

async function defaultFetcher() {
  return apiFetch('/api/agents/cost/current');
}

export default function useCostMeter(options = {}) {
  const pollInterval = options.pollIntervalMs || DEFAULT_POLL_MS;
  const fetcher = options.fetcher || defaultFetcher;
  const enabled = options.enabled !== false;

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);

  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher();
      if (!mountedRef.current) return { ok: false, error: 'unmounted' };
      if (!res.ok) {
        const m = (res.data && res.data.error) || res.error || 'cost fetch failed';
        setError(m);
        return { ok: false, error: m, status: res.status };
      }
      const data = res.data || res;
      setSnapshot(data);
      return { ok: true, data };
    } catch (err) {
      const m = err && err.message ? err.message : String(err);
      if (mountedRef.current) setError(m);
      return { ok: false, error: m };
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetcher]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
  }, []);

  const tick = useCallback(async () => {
    await refresh();
    if (!mountedRef.current) return;
    if (timerRef.current === null) return; // stopped during await
    timerRef.current = setTimeout(tick, pollInterval);
  }, [refresh, pollInterval]);

  const start = useCallback(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setPolling(true);
    timerRef.current = setTimeout(tick, 0); // fire immediately
  }, [enabled, tick]);

  useEffect(() => {
    if (!enabled) {
      stop();
      return undefined;
    }
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    snapshot,
    loading,
    error,
    polling,
    refresh,
    start,
    stop,
    alert: !!(snapshot && snapshot.alert),
    warning: !!(snapshot && snapshot.warning),
    pct: snapshot && Number.isFinite(Number(snapshot.pct)) ? Number(snapshot.pct) : 0,
  };
}
